import { useState } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import { type PluginEntry, pluginsApi } from "@/api/plugins-api"
import { Building2, Check, KeyRound, Loader2, Plug, Search } from "@/components/icons"
import { ModulePageHeader } from "@/components/layouts/module-page-header"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { EmptyState } from "@/components/ui/empty-state"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import { BackgroundAccountPanel } from "./background-account-panel"
import { ConnectionGrants } from "./connection-grants"
import { pluginErrorCode, pluginErrorMessage, startAuthorization } from "./plugin-utils"
import { usePluginQueryKey, usePluginSessionGeneration } from "./use-plugin-query-key"

export function PluginsPage() {
  const generation = usePluginSessionGeneration()
  return <PluginsPageContent key={generation} />
}

function PluginsPageContent() {
  const { t } = useTranslation()
  const pluginKey = usePluginQueryKey()
  const client = useQueryClient()
  const [query, setQuery] = useState("")
  const [mine, setMine] = useState(false)
  const [selected, setSelected] = useState<PluginEntry | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [mode, setMode] = useState<"install" | "connect" | "remove" | "upgrade">("install")
  const [secret, setSecret] = useState("")
  const catalog = useQuery({ queryKey: pluginKey("catalog"), queryFn: pluginsApi.catalog })
  const upgradeOperations = useQuery({
    queryKey: pluginKey("upgrade-operations", selected?.id, selected?.latest_version_id),
    queryFn: () => pluginsApi.operations(selected!.id, selected!.latest_version_id!),
    enabled: dialogOpen && mode === "upgrade" && Boolean(selected),
  })
  const action = useMutation({
    mutationFn: async () => {
      if (!selected) return
      if (mode === "install" || mode === "upgrade")
        return pluginsApi.install(selected.id, selected.latest_version_id!, mode === "upgrade")
      if (mode === "remove") return pluginsApi.uninstall(selected.id)
      if (selected.auth_kind === "oauth" || selected.auth_kind === "entra") {
        const result = await pluginsApi.startOAuth(selected.id)
        startAuthorization(result.authorization_url)
        return
      }
      const credential = secret
      setSecret("")
      return pluginsApi.connect(
        selected.id,
        selected.auth_kind === "api_key" ? "api_key" : "bearer",
        credential
      )
    },
    onSuccess: async () => {
      setDialogOpen(false)
      setSecret("")
      await client.invalidateQueries({ queryKey: pluginKey() })
    },
  })
  const disconnect = useMutation({
    mutationFn: pluginsApi.disconnect,
    onSuccess: () => client.invalidateQueries({ queryKey: pluginKey() }),
  })
  const error = action.error ?? disconnect.error ?? catalog.error
  const items = (catalog.data?.items ?? []).filter(
    (entry) =>
      (!mine || entry.installed) &&
      `${entry.name} ${entry.description}`.toLowerCase().includes(query.toLowerCase())
  )
  function open(entry: PluginEntry, next: typeof mode) {
    action.reset()
    setSecret("")
    setMode(next)
    setSelected(entry)
    setDialogOpen(true)
  }
  const kindLabels = { openapi: "Custom API", mcp: "MCP", native: t("plugins.tool") }
  const descriptions = {
    remove: t("plugins.removeDescription"),
    connect: t("plugins.connectDescription"),
    install: t("plugins.installDescription"),
    upgrade: t("plugins.installDescription"),
  }
  const actionLabels = {
    remove: t("plugins.uninstall"),
    connect: t("plugins.connect"),
    install: t("plugins.install"),
    upgrade: t("plugins.update"),
  }
  const reviewedPermissions =
    mode === "upgrade" ? selected?.latest_permissions : selected?.permissions
  const reviewedOperationCount =
    mode === "upgrade" ? selected?.latest_operation_count : selected?.operation_count
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-auto" data-testid="plugins-page">
      <ModulePageHeader subtitle={t("plugins.subtitle")} title={t("plugins.title")} />
      <div className="space-y-5 px-4 pb-8 sm:px-6">
        {catalog.data?.enabled && <BackgroundAccountPanel />}
        <div className="flex flex-wrap items-center gap-3">
          <div aria-label={t("plugins.view")} className="flex gap-2">
            <Button
              aria-pressed={!mine}
              variant={mine ? "outline" : "default"}
              onClick={() => setMine(false)}
            >
              {t("plugins.catalog")}
            </Button>
            <Button
              aria-pressed={mine}
              variant={mine ? "default" : "outline"}
              onClick={() => setMine(true)}
            >
              {t("plugins.mine")}
            </Button>
          </div>
          <div className="relative min-w-48 flex-1">
            <Search aria-hidden className="text-muted-foreground absolute top-3 left-3 size-4" />
            <Input
              aria-label={t("plugins.search")}
              className="pl-9"
              placeholder={t("plugins.search")}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
        </div>
        {error && (
          <p className="text-destructive text-sm" role="alert">
            {pluginErrorMessage(t, pluginErrorCode(error))}
          </p>
        )}
        {catalog.isPending && (
          <div className="flex items-center gap-2 py-10" role="status">
            <Loader2 className="size-5 animate-spin" />
            {t("plugins.loading")}
          </div>
        )}
        {catalog.error && (
          <Button variant="outline" onClick={() => void catalog.refetch()}>
            {t("plugins.refresh")}
          </Button>
        )}
        {!catalog.isPending && !catalog.error && !catalog.data.enabled && (
          <EmptyState
            description={t("plugins.disabledDescription")}
            icon={Building2}
            title={t("plugins.disabled")}
            primaryAction={
              <Button variant="outline" onClick={() => void catalog.refetch()}>
                {t("plugins.refresh")}
              </Button>
            }
          />
        )}
        {!catalog.isPending && catalog.data?.enabled && !items.length && (
          <EmptyState
            description={t("plugins.emptyDescription")}
            icon={Plug}
            title={t("plugins.empty")}
            primaryAction={
              <Button
                variant="outline"
                onClick={() => {
                  setMine(false)
                  setQuery("")
                }}
              >
                {t("plugins.browse")}
              </Button>
            }
          />
        )}
        {!catalog.isPending && catalog.data?.enabled && items.length > 0 && (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {items.map((entry) => (
              <Card key={entry.id} className="flex flex-col rounded-lg" data-testid="plugin-card">
                <CardHeader className="space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <Plug className="text-muted-foreground size-5" />
                    <Badge className="rounded-md" variant="secondary">
                      {kindLabels[entry.plugin_kind]}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg">{entry.name}</CardTitle>
                  <p className="text-muted-foreground text-xs">{entry.publisher}</p>
                </CardHeader>
                <CardContent className="flex flex-1 flex-col gap-4">
                  <p className="text-muted-foreground flex-1 text-sm">{entry.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {entry.managed && (
                      <Badge className="rounded-md" variant="outline">
                        <Building2 className="mr-1 size-3" />
                        {t("plugins.managed")}
                      </Badge>
                    )}
                    {entry.installed && (
                      <Badge className="rounded-md" variant="outline">
                        <Check className="mr-1 size-3" />
                        {t("plugins.installed")}
                      </Badge>
                    )}
                  </div>
                  {entry.connection?.mode === "personal" &&
                    entry.connection.status === "connected" && (
                      <ConnectionGrants connectionId={entry.connection.id} />
                    )}
                  {entry.installed && entry.auth_kind !== "none" && (
                    <p className="text-muted-foreground flex items-center gap-2 text-sm">
                      <KeyRound className="size-4" />
                      {entry.credential_mode === "company"
                        ? t("plugins.companyAccount")
                        : t(`plugins.connection.${entry.connection?.status ?? "disconnected"}`)}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {!entry.installed && entry.self_install && (
                      <Button onClick={() => open(entry, "install")}>{t("plugins.install")}</Button>
                    )}
                    {entry.installed &&
                      entry.credential_mode === "personal" &&
                      entry.auth_kind !== "none" && (
                        <Button variant="outline" onClick={() => open(entry, "connect")}>
                          {entry.connection?.status === "connected"
                            ? t("plugins.reconnect")
                            : t("plugins.connect")}
                        </Button>
                      )}
                    {entry.installed && entry.update_available && !entry.managed && (
                      <Button variant="outline" onClick={() => open(entry, "upgrade")}>
                        {t("plugins.update")}
                      </Button>
                    )}
                    {entry.connection?.status === "connected" &&
                      entry.connection.mode === "personal" && (
                        <Button
                          disabled={disconnect.isPending}
                          variant="ghost"
                          onClick={() => disconnect.mutate(entry.connection!.id)}
                        >
                          {t("plugins.disconnect")}
                        </Button>
                      )}
                    {entry.installed && !entry.managed && (
                      <Button variant="ghost" onClick={() => open(entry, "remove")}>
                        {t("plugins.uninstall")}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDialogOpen(false)
            setSecret("")
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selected?.name}</DialogTitle>
            <DialogDescription>{descriptions[mode]}</DialogDescription>
          </DialogHeader>
          {selected && mode !== "remove" && (
            <div className="space-y-3 text-sm">
              <p>{selected.description}</p>
              <p>{t("plugins.operations", { count: reviewedOperationCount })}</p>
              {mode === "upgrade" && selected.latest_endpoint && (
                <p className="text-sm break-all">{selected.latest_endpoint}</p>
              )}
              {mode === "upgrade" && (
                <ul className="max-h-40 overflow-auto text-sm">
                  {upgradeOperations.data?.operations.map((operation) => (
                    <li key={operation.id}>
                      {operation.id}: {operation.description}
                    </li>
                  ))}
                </ul>
              )}
              {mode === "upgrade" && upgradeOperations.isPending && (
                <p role="status">{t("plugins.loading")}</p>
              )}
              {mode === "upgrade" && upgradeOperations.error && (
                <p className="text-destructive" role="alert">
                  {pluginErrorMessage(t, pluginErrorCode(upgradeOperations.error))}
                </p>
              )}
              {Boolean(reviewedPermissions?.length) && (
                <div>
                  <p className="font-medium">{t("plugins.permissions")}</p>
                  <ul className="list-inside list-disc text-sm">
                    {reviewedPermissions?.map((scope) => (
                      <li key={scope}>{scope}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          {selected &&
            mode === "connect" &&
            (selected.auth_kind === "api_key" || selected.auth_kind === "bearer") && (
              <div className="space-y-2">
                <Label htmlFor="plugin-secret">{t("plugins.credential")}</Label>
                <Input
                  autoComplete="off"
                  id="plugin-secret"
                  type="password"
                  value={secret}
                  onChange={(event) => setSecret(event.target.value)}
                />
              </div>
            )}
          {action.error && (
            <p className="text-destructive text-sm" role="alert">
              {pluginErrorMessage(t, pluginErrorCode(action.error))}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDialogOpen(false)
                setSecret("")
              }}
            >
              {t("plugins.cancel")}
            </Button>
            <Button
              disabled={
                action.isPending ||
                (mode === "upgrade" &&
                  (upgradeOperations.isPending || upgradeOperations.isError)) ||
                (mode === "connect" &&
                  (selected?.auth_kind === "api_key" || selected?.auth_kind === "bearer") &&
                  !secret)
              }
              onClick={() => action.mutate()}
            >
              {action.isPending ? <Loader2 className="size-4 animate-spin" /> : actionLabels[mode]}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
